import { useState, useEffect } from "react";
import MainStatCard from "../../../components/analytics/MainStatCard";
import SectionAnalytics from "../../../components/analytics/SectionAnalytics";
import BasicPipelineDailyChart from "../../../components/analytics/BasicPipelineDailyChart";
import CategoryBarChartCard from "../../../components/analytics/CategoryBarChartCard";
import { ChartSkeleton, StatSkeleton } from "../../../components/analytics/ChartSkeleton";
import { trpc } from "../../../trpc/index";
import { formatDecimal } from "../../../utils";
import { mapTicketStatus } from "../../../utils/valueMap";

type Props = {
  startDate: string;
  endDate: string;
  merchantId: string;
  pipelineId: "maintenance" | "sourceproperties";
  successLabel: string;
  successInfo: string;
  showTicketStatus: boolean;
};

const AnalyticsBasicPipelineContainer = ({
  startDate,
  endDate,
  merchantId,
  pipelineId,
  successLabel,
  successInfo,
  showTicketStatus,
}: Props) => {
  const [fetching, setFetching] = useState(true);

  const rangeInput = { merchantId, startDate, endDate };

  const { data: summaryData, refetch: refetchSummary } =
    trpc.analytics.adminPipelineSummary.useQuery(
      { ...rangeInput, pipelineId },
      {
        enabled: false,
        refetchOnWindowFocus: false,
        onSuccess: () => {
          setFetching(false);
        },
        onError: () => {
          setFetching(false);
        },
      }
    );

  const { data: dailyData, refetch: refetchDaily } = trpc.analytics.adminPipelineDaily.useQuery(
    { ...rangeInput, pipelineId },
    { enabled: false, refetchOnWindowFocus: false }
  );

  const { data: ticketStatusData, refetch: refetchTicketStatus } =
    trpc.analytics.adminTicketStatus.useQuery(
      { ...rangeInput, pipelineId: "maintenance" },
      { enabled: false, refetchOnWindowFocus: false }
    );

  // Ticket statuses are merchant-configurable pipeline ticketPositions —
  // resolve display names from config (mapTicketStatus is only a fallback).
  const { data: ticketPositionsByStage } = trpc.pipeline.getTicketPositions.useQuery(
    { merchantId, pipelineId },
    { enabled: !!merchantId && showTicketStatus, refetchOnWindowFocus: false }
  );
  const ticketStatusLabels = Object.fromEntries(
    Object.values(ticketPositionsByStage ?? {})
      .flat()
      .map((position) => [position.id, position.displayName])
  );

  useEffect(() => {
    if (merchantId && startDate && endDate) {
      setFetching(true);

      refetchSummary();
      refetchDaily();
      if (showTicketStatus) refetchTicketStatus();
    }
  }, [merchantId, startDate, endDate, pipelineId, showTicketStatus]);

  if (fetching) {
    return (
      <div className="flex h-full grow flex-col gap-6">
        <SectionAnalytics title="Resumen" gridClassName="grid-cols-1 md:grid-cols-3">
          <StatSkeleton />
          <StatSkeleton />
          <StatSkeleton />
        </SectionAnalytics>
        <SectionAnalytics title="Evolución diaria" gridClassName="grid-cols-1">
          <ChartSkeleton height="h-[260px]" />
        </SectionAnalytics>
        {showTicketStatus && (
          <SectionAnalytics title="Solicitudes" gridClassName="grid-cols-1 md:grid-cols-2">
            <ChartSkeleton height="h-[260px]" />
          </SectionAnalytics>
        )}
      </div>
    );
  }

  const totalLeads = summaryData?.totalLeads ?? 0;
  const successCount = summaryData?.successCount ?? 0;
  const conversionRate = summaryData?.conversionRate ?? 0;

  const ticketStatusChartData = (ticketStatusData ?? []).map((row) => ({
    name: ticketStatusLabels[row.status] ?? mapTicketStatus[row.status] ?? row.status,
    value: row.count,
  }));

  return (
    <div className="flex h-full grow flex-col gap-6">
      <SectionAnalytics title="Resumen" gridClassName="grid-cols-1 md:grid-cols-3">
        <MainStatCard
          title="Total conversaciones"
          value={totalLeads}
          info="Conversaciones de este embudo iniciadas en el período"
        />
        <MainStatCard title={successLabel} value={successCount} info={successInfo} />
        <MainStatCard
          title="Tasa de conversión"
          value={totalLeads > 0 ? `${formatDecimal(conversionRate, 0)}%` : "—"}
          info="Porcentaje de conversaciones que llegaron al resultado exitoso"
        />
      </SectionAnalytics>

      <SectionAnalytics title="Evolución diaria" gridClassName="grid-cols-1">
        <BasicPipelineDailyChart
          data={dailyData ?? []}
          successLabel={successLabel}
          subtitle={`Conversaciones y ${successLabel.toLowerCase()} por día`}
          info={`Conversaciones de este embudo iniciadas cada día y cuántas de ellas llegaron a "${successLabel}"`}
        />
      </SectionAnalytics>

      {showTicketStatus && (
        <SectionAnalytics title="Solicitudes" gridClassName="grid-cols-1 md:grid-cols-2">
          <CategoryBarChartCard
            title="Estado de tickets"
            subtitle="Solicitudes del período según su estado actual"
            info="Todas las solicitudes del período según el estado en que están hoy — tanto las que la IA resolvió directamente como las escaladas a asesores"
            data={ticketStatusChartData}
            orientation="horizontal"
            emptyText="Sin datos en este período"
            unitLabel={{ singular: "solicitud", plural: "solicitudes" }}
          />
        </SectionAnalytics>
      )}
    </div>
  );
};

export default AnalyticsBasicPipelineContainer;
